import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Target, Heart, Users, Award, TrendingUp, Shield, 
  CheckCircle, ArrowRight, Sparkles, Building2, Phone,
  Star, Clock, Zap, MapPin, Calendar
} from 'lucide-react';

const HakkimizdaPage = () => {
  const stats = [
    { value: '50.000+', label: 'Mutlu Müşteri', icon: Heart },
    { value: '500+', label: 'Uzman Usta', icon: Users },
    { value: '100.000+', label: 'Tamamlanan İş', icon: CheckCircle },
    { value: '4.8/5', label: 'Müşteri Puanı', icon: Star },
  ];

  const values = [
    {
      icon: Shield,
      title: 'Güvenilirlik',
      description: 'Tüm ustalarımız kimlik doğrulaması ve yetkinlik testinden geçirilir. Her hizmet garantimiz altındadır.',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Zap,
      title: 'Hız',
      description: 'Acil ihtiyaçlarınız için aynı gün hizmet. Ortalama 2 saat içinde ustanız kapınızda.',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: Heart,
      title: 'Müşteri Odaklılık',
      description: 'Memnuniyetiniz bizim önceliğimiz. 7/24 destek hattımız her zaman yanınızda.',
      color: 'from-rose-500 to-pink-600'
    },
    {
      icon: Award,
      title: 'Kalite',
      description: 'En iyi malzemeler, en güncel teknikler. İşimizi en iyi şekilde yapmak için sürekli kendimizi geliştiriyoruz.',
      color: 'from-emerald-500 to-teal-600'
    },
  ];

  const timeline = [
    { year: '2020', title: 'Kuruluş', desc: 'Ankara\'da küçük bir ekiple yola çıktık.' },
    { year: '2021', title: 'Büyüme', desc: '100 usta ile 10.000 hizmet tamamladık.' },
    { year: '2022', title: 'Genişleme', desc: 'Türkiye genelinde hizmet vermeye başladık.' },
    { year: '2023', title: 'Dijitalleşme', desc: 'Yapay zeka destekli fiyatlandırma sistemini devreye aldık.' },
    { year: '2024', title: 'Liderlik', desc: '500+ usta ile sektörde yerimizi aldık.' },
  ];

  const team = [
    { role: 'Müşteri Hizmetleri', count: '25+', desc: '7/24 destek ekibi' },
    { role: 'Teknik Destek', count: '15+', desc: 'Usta koordinasyon' },
    { role: 'Kalite Kontrol', count: '10+', desc: 'Hizmet denetimi' },
    { role: 'Yazılım Geliştirme', count: '20+', desc: 'Platform geliştirme' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-[#006D77] via-[#0A9396] to-[#006D77] overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2.5 mb-6">
                <Building2 className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">Hakkımızda</span>
              </div>
              
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Türkiye'nin En Güvenilir<br />
                <span className="text-[#F6AD55]">Ev Hizmetleri Platformu</span>
              </h1>
              
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                2020 yılından bu yana binlerce aileye güvenilir, kaliteli ve uygun fiyatlı 
                ev hizmetleri sunuyoruz. Misyonumuz, ev sahiplerini alanında uzman ustalarla 
                buluşturarak hayatı kolaylaştırmak.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/hizmetler">
                  <Button className="bg-white text-[#006D77] hover:bg-gray-100 px-6 py-3 rounded-full font-semibold">
                    Hizmetlerimiz
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/iletisim">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold">
                    İletişime Geç
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/44e85891-56c7-47e6-9e22-5549e752d665/images/fcd6b833af5de66b83e6aa868db6a6927acbb4654113daaae8cd95ffb7ef7f03.png"
                  alt="HizmetGeliyo Ofis"
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">100.000+</p>
                      <p className="text-gray-500 text-sm">Tamamlanan İş</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 -mt-8 relative z-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 text-center hover:shadow-2xl transition-shadow">
                  <Icon className="w-8 h-8 text-[#006D77] mx-auto mb-3" />
                  <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#006D77]/10 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-[#006D77]" />
                <span className="text-[#006D77] text-sm font-semibold">Hikayemiz</span>
              </div>
              
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Bir Hayalle Başladık,<br />
                <span className="text-[#006D77]">Binlerce Aileye Ulaştık</span>
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  HizmetGeliyo, 2020 yılında Ankara'da küçük bir ekiple kuruldu. Amacımız basitti: 
                  İnsanların güvenilir usta bulmakta yaşadığı zorlukları ortadan kaldırmak.
                </p>
                <p>
                  Kısa sürede büyüyerek Türkiye'nin dört bir yanına ulaştık. Bugün 500'den fazla 
                  onaylı usta ile kombi, klima, beyaz eşya, elektrik ve su tesisatı alanlarında 
                  profesyonel hizmet sunuyoruz.
                </p>
                <p>
                  Yapay zeka destekli fiyatlandırma sistemimiz, şeffaf iletişim anlayışımız ve 
                  %100 memnuniyet garantimiz ile sektörde fark yaratıyoruz. Her gün daha iyiye 
                  ulaşmak için çalışıyoruz.
                </p>
              </div>

              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#E29578]" />
                  <span className="text-gray-700 font-medium">Ankara Merkez</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#E29578]" />
                  <span className="text-gray-700 font-medium">2020'den beri</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/44e85891-56c7-47e6-9e22-5549e752d665/images/191a34b67a12e4219978a8d56df8909f354b0b682e8732c294d46a0480002101.png"
                alt="HizmetGeliyo Ekibi"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -top-4 -right-4 bg-[#006D77] text-white rounded-2xl p-4 shadow-xl">
                <p className="text-3xl font-bold">4+</p>
                <p className="text-white/80 text-sm">Yıllık Deneyim</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#006D77]/10 rounded-full px-4 py-2 mb-4">
              <Target className="w-4 h-4 text-[#006D77]" />
              <span className="text-[#006D77] text-sm font-semibold">Değerlerimiz</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Bizi Biz Yapan Değerler
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Her kararımızda, her hizmetimizde bu değerleri rehber ediniyoruz
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                  <div className={`w-14 h-14 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#006D77]/10 rounded-full px-4 py-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#006D77]" />
              <span className="text-[#006D77] text-sm font-semibold">Yolculuğumuz</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Büyüme Hikayemiz
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-[#006D77] via-[#E29578] to-[#006D77] rounded-full"></div>
            
            <div className="grid md:grid-cols-5 gap-6">
              {timeline.map((item, idx) => (
                <div key={idx} className="relative text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10">
                    <span className="text-white font-bold">{item.year}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#006D77]/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-4">
              <Users className="w-4 h-4 text-[#F6AD55]" />
              <span className="text-[#F6AD55] text-sm font-semibold">Ekibimiz</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Profesyonel Kadromuz
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              70+ kişilik merkez ekibimiz ile 7/24 hizmetinizdeyiz
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {team.map((member, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-colors">
                <p className="text-4xl font-bold text-[#F6AD55] mb-2">{member.count}</p>
                <h3 className="text-white font-bold mb-1">{member.role}</h3>
                <p className="text-white/60 text-sm">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-3xl p-8 md:p-10 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Misyonumuz</h3>
              <p className="text-white/80 leading-relaxed">
                Ev sahiplerini alanında uzman, güvenilir ve dürüst ustalarla buluşturarak, 
                ev hizmetleri alanında yaşanan güven sorununu ortadan kaldırmak. Her hizmette 
                kalite, şeffaflık ve müşteri memnuniyetini ön planda tutmak.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E29578] to-[#D67A56] rounded-3xl p-8 md:p-10 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Vizyonumuz</h3>
              <p className="text-white/80 leading-relaxed">
                Türkiye'nin en büyük ve en güvenilir ev hizmetleri platformu olmak. 
                Teknoloji ve insan odaklı yaklaşımımızla sektörde standartları belirlemek 
                ve her eve ulaşarak hayatı kolaylaştırmak.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Hizmet Almaya Hazır mısınız?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-xl mx-auto">
            Binlerce memnun müşterimize katılın. Güvenilir ustalarımız ile tanışın.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/hizmetler">
              <Button className="bg-[#006D77] hover:bg-[#005a63] text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg">
                Hizmet Al
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a 
              href="tel:08502427808"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#006D77] text-[#006D77] hover:bg-[#006D77] hover:text-white px-8 py-4 rounded-full text-lg font-semibold transition-all"
            >
              <Phone className="w-5 h-5" />
              0850 242 78 08
            </a>
          </div>
        </div>
      </section>

      {/* Kurumsal Bilgiler */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#006D77]/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#006D77]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Kurumsal Bilgiler</h3>
            </div>
            
            <div className="space-y-4 text-gray-600">
              <p>
                <strong className="text-gray-900">hizmetgeliyo.com</strong>, Moda Ekru E-Ticaret Hizmetleri Tekstil Sanayi Limited Şirketi kuruluşudur.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                <p>
                  <span className="text-gray-500">Vergi No:</span>{' '}
                  <strong className="text-gray-900">6222391169</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HakkimizdaPage;
